import React, { useState } from "react";
import { BaseDialog } from "components/common/Dialogs";
import { TextField } from "components/common/Form";
import { Error } from "components/common/ControlElems";
import { getApiErrorMessage } from "context/helpers/contentContextHelper";
import styles from "./ReturningMembers.module.scss";

const PRESET_COUNTS = [3, 10, 25, 50, 100];
const SEND_ALL = "all";
const MIN_SEND_COUNT = 1;
const MAX_SEND_COUNT = 10000;

export default function StartCampaignDialog({
    processing,
    error,
    defaultCount = 50,
    availableCount,
    onClose,
    onConfirm
}) {
    const initialCount = Number.isFinite(Number(defaultCount))
        ? String(defaultCount)
        : "50";
    const [sendAll, setSendAll] = useState(false);
    const [count, setCount] = useState(initialCount);
    const [validationError, setValidationError] = useState("");

    const handlePresetClick = value => {
        setValidationError("");
        if (value === SEND_ALL) {
            setSendAll(true);
            return;
        }
        setSendAll(false);
        setCount(String(value));
    };

    const handleCountChange = event => {
        setSendAll(false);
        setValidationError("");
        setCount(event.target.value);
    };

    const handleConfirm = () => {
        if (sendAll) {
            onConfirm({ sendToAll: true });
            return;
        }
        const parsed = Number.parseInt(count, 10);
        if (
            !Number.isFinite(parsed) ||
            parsed < MIN_SEND_COUNT ||
            parsed > MAX_SEND_COUNT
        ) {
            setValidationError(
                `Enter how many people to send, from ${MIN_SEND_COUNT} to ${MAX_SEND_COUNT}.`
            );
            return;
        }
        onConfirm({ sendToAll: false, limit: parsed });
    };

    return (
        <BaseDialog
            id="startReturningMemberCampaignDialog"
            label="Create Campaign"
            onClose={onClose}
        >
            <div className={styles["send-offer-dialog"]}>
                <p className={styles["send-offer-confirm"]}>
                    Choose or enter how many returning members should receive
                    the offer.
                </p>
                {availableCount > 0 && (
                    <p className={styles["campaign-available"]}>
                        Current list total: <strong>{availableCount}</strong>
                    </p>
                )}

                <div className={styles["campaign-presets"]}>
                    {PRESET_COUNTS.map(value => (
                        <button
                            key={value}
                            type="button"
                            className={`btn ${
                                !sendAll && String(count) === String(value)
                                    ? "btn-primary"
                                    : "btn-secondary"
                            }`}
                            disabled={processing}
                            onClick={() => handlePresetClick(value)}
                        >
                            {value}
                        </button>
                    ))}
                    <button
                        type="button"
                        className={`btn ${
                            sendAll ? "btn-primary" : "btn-secondary"
                        }`}
                        disabled={processing}
                        onClick={() => handlePresetClick(SEND_ALL)}
                    >
                        All
                    </button>
                </div>

                {!sendAll && (
                    <TextField
                        value={count}
                        name="campaignSendCount"
                        label="Number of people"
                        type="number"
                        min={MIN_SEND_COUNT}
                        max={MAX_SEND_COUNT}
                        placeholder="e.g. 50"
                        disabled={processing}
                        onChange={handleCountChange}
                    />
                )}
                {sendAll && (
                    <p className={styles["send-offer-confirm"]}>
                        This will queue the offer for all returning members.
                    </p>
                )}
                <p className={styles["campaign-qa-note"]}>
                    On local/QA only 3 test emails are sent.
                </p>

                {validationError && <Error text={validationError} />}
                {error && (
                    <Error
                        text={getApiErrorMessage(
                            error,
                            "Failed to start the returning member campaign."
                        )}
                    />
                )}
                <div className="d-flex flex-row justify-content-between mt-3">
                    <button
                        type="button"
                        className="btn btn-secondary w-25"
                        onClick={onClose}
                        disabled={processing}
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        className="btn btn-primary w-50"
                        onClick={handleConfirm}
                        disabled={processing}
                    >
                        {processing ? "Creating..." : "Create Campaign"}
                    </button>
                </div>
            </div>
        </BaseDialog>
    );
}
